import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Input, Button, Message, Spin, Alert, Space } from '@arco-design/web-react';
import { IconLock, IconEmail, IconUser, IconCheck } from '@arco-design/web-react/icon';
import { useQuery, useMutation } from '@tanstack/react-query';
import { apiClient } from '@/services/apiClient';
import { API_ENDPOINTS } from '@/config/apiEndpoints';
import { useTranslation } from 'react-i18next';
import PublicLayout from './PublicLayout';
import { formatRoleLabel } from '@/modules/common/helpers/roleLabels';

const FormItem = Form.Item;

function InvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [submitError, setSubmitError] = useState(null);
  const { t } = useTranslation('public');
  const directBaseUrl = typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? 'http://localhost:8010'
    : null;

  const getInviteErrorMessage = (reason) => {
    const key = reason || 'default';
    return t(`registration.inviteErrors.${key}`, t('registration.inviteErrors.default'));
  };

  // Validate invitation token with React Query
  const { data: validationData, isLoading: loading } = useQuery({
    queryKey: ['invitation', 'validate', token],
    queryFn: async () => {
      const response = await apiClient.get(API_ENDPOINTS.PUBLIC_INVITE_VALIDATE, { token });
      const data = response?.data;
      if (data?.valid) {
        return { invitation: data.invitation, error: null, errorReason: null };
      }
      const reason = data?.reason;
      return { invitation: null, error: getInviteErrorMessage(reason), errorReason: reason || null };
    },
    enabled: !!token,
    retry: 1,
    staleTime: 5 * 60 * 1000,
    onSuccess: (data) => {
      if (data?.invitation?.email) {
        form.setFieldValue('email', data.invitation.email);
      }
    },
  });

  const invitation = validationData?.invitation || null;
  const error = !token ? t('registration.invalidToken') : (validationData?.error || null);
  const errorReason = validationData?.errorReason || null;

  // Register mutation
  const registerMutation = useMutation({
    mutationFn: async (payload) => {
      try {
        return await apiClient.post(API_ENDPOINTS.PUBLIC_REGISTER, {}, payload);
      } catch (err) {
        const isTimeout = err?.code === 'ECONNABORTED';
        if (isTimeout && directBaseUrl) {
          return await apiClient.post(API_ENDPOINTS.PUBLIC_REGISTER, {}, payload, { baseURL: directBaseUrl });
        }
        throw err;
      }
    },
    onSuccess: (response) => {
      const data = response?.data;
      if (data?.success) {
        Message.success(t('auth:register.messages.accountCreated'));
        setTimeout(() => navigate('/login'), 1500);
      } else {
        const message = data?.message || t('auth:errors.couldNotCreateAccount');
        setSubmitError(message);
        Message.error(message);
      }
    },
    onError: (err) => {
      const isTimeout = err?.code === 'ECONNABORTED';
      const message = isTimeout
        ? t('auth:errors.serverTimeout')
        : (err.response?.data?.detail || t('auth:errors.couldNotCreateAccount'));
      setSubmitError(message);
      Message.error(message);
    },
  });

  const handleSubmit = (values) => {
    if (!invitation) {
      Message.error(t('auth:register.messages.noValidInvitation'));
      return;
    }
    setSubmitError(null);
    registerMutation.mutate({
      email: values.email,
      password: values.password,
      invitation_token: token
    });
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="public-card" style={{ textAlign: 'center', padding: '60px 40px' }}>
          <Spin size={40} />
          <p style={{ marginTop: 16, color: '#86909c' }}>{t('registration.validating')}</p>
        </div>
      </PublicLayout>
    );
  }

  if (error) {
    return (
      <PublicLayout>
        <div className="public-card">
          <div className="public-card-header">
            <h1>{t('registration.invalidTitle')}</h1>
          </div>
          <Alert
            type="error"
            content={error}
            style={{ marginBottom: 24 }}
          />
          {errorReason === 'used' ? (
            <Button
              type="primary"
              long
              onClick={() => navigate('/login')}
            >
              {t('registration.goToLogin')}
            </Button>
          ) : (
            <Button
              type="primary"
              long
              onClick={() => navigate('/catalogo')}
            >
              {t('registration.goToCatalog')}
            </Button>
          )}
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="public-card">
        <div className="public-card-header">
          <h1>{t('registration.welcome')}</h1>
          <p>{t('registration.completeRegistration')}</p>
        </div>

        {invitation && (
          <Alert
            type="info"
            content={
              <Space direction="vertical" size="small">
                <div>
                  <IconCheck style={{ marginRight: 8 }} />
                  {t('registration.validInvitation')} <strong>{formatRoleLabel(invitation.role?.name) || t('registration.defaultRole')}</strong>
                </div>
                {invitation.contract_modality && (
                  <div>
                    <IconUser style={{ marginRight: 8 }} />
                    {t('registration.modality')} <strong>{invitation.contract_modality}</strong>
                  </div>
                )}
              </Space>
            }
            style={{ marginBottom: 24 }}
          />
        )}

        <Form
          form={form}
          layout="vertical"
          onSubmit={handleSubmit}
          autoComplete="off"
        >
          {submitError && (
            <Alert
              type="error"
              content={submitError}
              style={{ marginBottom: 16 }}
            />
          )}
          <FormItem
            label={t('registration.email')}
            field="email"
            rules={[
              { required: true, message: t('registration.emailRequired') },
              { type: 'email', message: t('registration.emailInvalid') }
            ]}
          >
            <Input
              prefix={<IconEmail />}
              placeholder={t('registration.emailPlaceholder')}
              readOnly={!!invitation?.email}
              className={invitation?.email ? 'public-input-readonly' : undefined}
              size="large"
            />
          </FormItem>

          <FormItem
            label={t('registration.password')}
            field="password"
            rules={[
              { required: true, message: t('registration.passwordRequired') },
              { minLength: 8, message: t('registration.passwordMin') },
              {
                validator: (value, callback) => {
                  if (!value) {
                    callback();
                    return;
                  }

                  const hasUppercase = /[A-Z]/.test(value);
                  const hasLowercase = /[a-z]/.test(value);
                  const hasNumber = /[0-9]/.test(value);

                  if (!hasUppercase) {
                    callback(t('registration.passwordUppercase'));
                  } else if (!hasLowercase) {
                    callback(t('registration.passwordLowercase'));
                  } else if (!hasNumber) {
                    callback(t('registration.passwordNumber'));
                  } else {
                    callback();
                  }
                }
              }
            ]}
            extra={
              <div style={{ fontSize: 12, color: 'var(--color-text-3)', marginTop: 4 }}>
                {t('registration.passwordRequirements')}
                <ul style={{ margin: '4px 0 0 20px', padding: 0 }}>
                  <li>{t('registration.passwordReq1')}</li>
                  <li>{t('registration.passwordReq2')}</li>
                  <li>{t('registration.passwordReq3')}</li>
                  <li>{t('registration.passwordReq4')}</li>
                </ul>
              </div>
            }
          >
            <Input.Password
              prefix={<IconLock />}
              placeholder={t('registration.passwordPlaceholder')}
              size="large"
            />
          </FormItem>

          <FormItem
            label={t('registration.confirmPassword')}
            field="confirmPassword"
            rules={[
              { required: true, message: t('registration.confirmPasswordRequired') },
              {
                validator: (value, callback) => {
                  const password = form.getFieldValue('password');
                  if (value && value !== password) {
                    callback(t('registration.passwordsMismatch'));
                  } else {
                    callback();
                  }
                }
              }
            ]}
          >
            <Input.Password
              prefix={<IconLock />}
              placeholder={t('registration.confirmPasswordPlaceholder')}
              size="large"
            />
          </FormItem>

          <FormItem style={{ marginBottom: 0, marginTop: 32 }}>
            <Button
              type="primary"
              htmlType="submit"
              long
              size="large"
              loading={registerMutation.isPending}
            >
              {t('registration.createAccount')}
            </Button>
          </FormItem>
        </Form>

        <div style={{ marginTop: 24, textAlign: 'center', color: '#86909c', fontSize: 14 }}>
          {t('registration.alreadyHaveAccount')}{' '}
          <a href="/login" style={{ color: '#667eea', fontWeight: 500 }}>
            {t('registration.signIn')}
          </a>
        </div>
      </div>
    </PublicLayout>
  );
}

export default InvitePage;
